import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Sparkles, Star, Check, Gift, X, Mail, Loader2, ArrowRight, BookOpen } from "lucide-react";
import { api, IMG } from "../lib/api";
import { toast } from "sonner";

const rise = { initial: { opacity: 0, y: 40 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-80px" }, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } };

/* ---------- Promo countdown bar ---------- */
export function PromoBar({ lang }) {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    let end = Number(localStorage.getItem("bh_promo_end"));
    if (!end || end < Date.now()) { end = Date.now() + 6 * 3600 * 1000; localStorage.setItem("bh_promo_end", String(end)); }
    const tick = () => setLeft(Math.max(0, end - Date.now()));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  const h = String(Math.floor(left / 3600000)).padStart(2, "0");
  const m = String(Math.floor((left % 3600000) / 60000)).padStart(2, "0");
  const s = String(Math.floor((left % 60000) / 1000)).padStart(2, "0");
  return (
    <div className="fixed top-0 inset-x-0 z-[60] h-9 bg-[#1A243A] text-[#FDFBF7] flex items-center justify-center gap-3 text-xs sm:text-sm px-4" data-testid="promo-bar">
      <Sparkles size={13} className="text-[#D4AF37]" />
      <span className="font-medium">{lang === "pl" ? "-15% na pierwsze zamówienie" : "-15% on your first order"}</span>
      <span className="text-[#FDFBF7]/60 hidden sm:inline">·</span>
      <span className="text-[#D4AF37] hidden sm:inline">{lang === "pl" ? "kończy się za" : "ends in"}</span>
      <span className="font-mono tabular-nums bg-[#D4AF37] text-[#1A243A] rounded-md px-2 py-0.5 font-bold" data-testid="promo-countdown">{h}:{m}:{s}</span>
    </div>
  );
}

/* ---------- Book interior gallery ---------- */
export function InteriorGallery({ lang }) {
  const spreads = [
    { img: IMG.spread1, pl: "Pierwsza strona z dedykacją", en: "Opening page with a dedication" },
    { img: IMG.spread2, pl: "Bohater rozpoczyna przygodę", en: "The hero begins the adventure" },
    { img: IMG.cover1, pl: "Personalizowana okładka", en: "Personalized cover" },
    { img: IMG.spread3, pl: "Pełne rozkładówki z ilustracjami", en: "Full illustrated spreads" },
    { img: IMG.spread4, pl: "Szczegóły inspirowane dzieckiem", en: "Details inspired by your child" },
  ];
  return (
    <section className="py-24 sm:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.p {...rise} className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-bold">{lang === "pl" ? "Zajrzyj do środka" : "Look inside"}</motion.p>
        <motion.h2 {...rise} className="font-display text-4xl sm:text-5xl mt-3">{lang === "pl" ? "Zajrzyj do wnętrza książki" : "Peek inside the book"}</motion.h2>
        <motion.p {...rise} className="text-sm text-[#64748B] mt-2">{lang === "pl" ? "Przesuń w bok, aby zobaczyć więcej rozkładówek." : "Scroll sideways to see more spreads."}</motion.p>
      </div>
      <div className="mt-10 flex gap-6 overflow-x-auto hide-scrollbar snap-x snap-mandatory px-5 sm:px-8 pb-6" data-testid="interior-gallery">
        {spreads.map((sp, i) => (
          <div key={i} className="snap-center shrink-0 w-[80vw] sm:w-[520px] group" style={{ perspective: "1200px" }}>
            <div className="rounded-3xl overflow-hidden shadow-2xl border border-white transition-transform duration-500 group-hover:[transform:rotateY(-6deg)_rotateX(2deg)]">
              <img src={sp.img} alt={lang === "pl" ? sp.pl : sp.en} className="w-full aspect-[4/3] object-cover" />
            </div>
            <p className="mt-4 font-display text-xl text-[#1A243A]">{lang === "pl" ? sp.pl : sp.en}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------- Quality section ---------- */
export function QualitySection({ lang }) {
  const badges = lang === "pl"
    ? ["Gruby papier premium", "Szyta twarda oprawa", "Złocone akcenty", "Bezpieczne, nietoksyczne tusze"]
    : ["Thick premium paper", "Sewn hardcover binding", "Gold-foil accents", "Safe, non-toxic inks"];
  return (
    <section className="bg-[#1A243A] text-[#FDFBF7] py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center">
        <div className="grid grid-cols-2 gap-4">
          {[IMG.qualGold, IMG.qualPages, IMG.qualBind, IMG.spread4].map((src, i) => (
            <motion.div key={i} {...rise} transition={{ ...rise.transition, delay: i * 0.06 }} className={`rounded-2xl overflow-hidden border border-[#FDFBF7]/10 ${i % 3 === 0 ? "translate-y-4" : ""}`}>
              <img src={src} alt="" className="w-full h-full object-cover aspect-square" />
            </motion.div>
          ))}
        </div>
        <div>
          <motion.p {...rise} className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-bold">{lang === "pl" ? "Jakość wykonania" : "Craftsmanship"}</motion.p>
          <motion.h2 {...rise} className="font-display text-4xl sm:text-5xl mt-3 leading-tight">{lang === "pl" ? "Dopracowana w każdym detalu." : "Perfected in every detail."}</motion.h2>
          <motion.p {...rise} className="mt-5 text-[#FDFBF7]/80 leading-relaxed">{lang === "pl" ? "Każdy egzemplarz drukujemy na grubym papierze premium i oprawiamy w solidną twardą okładkę ze złoconymi akcentami — tak, aby książka przetrwała lata codziennego czytania." : "Every copy is printed on thick premium paper and bound in a sturdy hardcover with gold-foil accents — made to survive years of daily reading."}</motion.p>
          <div className="mt-7 grid sm:grid-cols-2 gap-3">
            {badges.map((b, i) => (
              <motion.div key={i} {...rise} transition={{ ...rise.transition, delay: i * 0.05 }} className="flex items-center gap-3 rounded-2xl border border-[#FDFBF7]/15 px-4 py-3">
                <Check size={16} className="text-[#D4AF37] shrink-0" /><span className="text-sm">{b}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Occasions ---------- */
export function OccasionsSection({ lang, onCta }) {
  const items = [
    { img: IMG.occBirthday, pl: "Urodziny", en: "Birthdays" },
    { img: IMG.occXmas, pl: "Święta", en: "Holidays" },
    { img: IMG.occBaby, pl: "Roczek i chrzciny", en: "First birthday & christening" },
    { img: IMG.occGrand, pl: "Prezent od dziadków", en: "A gift from grandparents" },
  ];
  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32">
      <motion.p {...rise} className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-bold">{lang === "pl" ? "Idealny prezent na okazje" : "The perfect gift for any occasion"}</motion.p>
      <motion.h2 {...rise} className="font-display text-4xl sm:text-5xl mt-3">{lang === "pl" ? "Na każdą wyjątkową chwilę" : "For every special moment"}</motion.h2>
      <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-5">
        {items.map((it, i) => (
          <motion.button key={i} {...rise} transition={{ ...rise.transition, delay: i * 0.06 }} onClick={onCta} data-testid={`occasion-${i}`} className="group relative rounded-3xl overflow-hidden aspect-[3/4] border border-white shadow-lg block">
            <img src={it.img} alt={lang === "pl" ? it.pl : it.en} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1A243A]/75 via-transparent to-transparent" />
            <span className="absolute bottom-4 left-4 right-4 font-display text-xl text-white text-left leading-tight">{lang === "pl" ? it.pl : it.en}</span>
          </motion.button>
        ))}
      </div>
    </section>
  );
}

/* ---------- Reviews marquee ---------- */
export function ReviewsMarquee({ lang }) {
  const reviews = [
    { n: "Anna K.", pl: "Córka nie wypuszcza książki z rąk.", en: "My daughter won't put the book down." },
    { n: "Marcin W.", pl: "Najlepszy prezent, jaki daliśmy synowi.", en: "The best gift we ever gave our son." },
    { n: "Ewa i Tomasz", pl: "Animowane sceny to był hit na urodzinach.", en: "The animated scenes were the party hit." },
    { n: "Karolina M.", pl: "Historia oparta na jej pasjach. Magia.", en: "A story built on her passions. Magic." },
    { n: "Paweł D.", pl: "Jakość druku naprawdę premium.", en: "The print quality is truly premium." },
    { n: "Magda S.", pl: "Syn poczuł się jak prawdziwy bohater.", en: "My son felt like a true hero." },
    { n: "Ola i Bartek", pl: "Dziadkowie popłakali się ze wzruszenia.", en: "The grandparents cried with joy." },
  ];
  const doubled = [...reviews, ...reviews];
  return (
    <section className="py-6 overflow-hidden bg-[#F5F2EB]/50 border-y border-[#E7E0D4]" data-testid="reviews-marquee">
      <div className="marquee-track gap-5">
        {doubled.map((r, i) => (
          <div key={i} className="shrink-0 w-[320px] rounded-2xl bg-white border border-[#E7E0D4] p-5">
            <div className="flex text-[#D4AF37]">{[...Array(5)].map((_, j) => <Star key={j} size={13} fill="#D4AF37" />)}</div>
            <p className="mt-3 text-sm text-[#1A243A]/85 leading-relaxed">„{lang === "pl" ? r.pl : r.en}”</p>
            <p className="mt-3 text-xs font-semibold text-[#64748B]">{r.n}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------- Newsletter ---------- */
export function NewsletterSection({ lang }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState("");
  const submit = async () => {
    if (!email.includes("@")) { toast.error(lang === "pl" ? "Podaj poprawny e-mail." : "Enter a valid email."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/newsletter", { email });
      setCode(data.code);
    } catch (e) {
      toast.error(lang === "pl" ? "Coś poszło nie tak." : "Something went wrong.");
    }
    setLoading(false);
  };
  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
      <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-[#E6D7FF] via-[#FDFBF7] to-[#F5F2EB] border border-[#D4AF37]/30 p-10 sm:p-14 text-center">
        <div className="spotlight absolute inset-0 pointer-events-none" />
        <div className="relative max-w-xl mx-auto">
          <Gift className="mx-auto text-[#D4AF37]" size={30} strokeWidth={1.5} />
          <h2 className="font-display text-3xl sm:text-4xl mt-4">{lang === "pl" ? "Odbierz 15% rabatu" : "Get 15% off"}</h2>
          <p className="mt-3 text-[#1A243A]/75">{lang === "pl" ? "Zapisz się, a wyślemy kod rabatowy na pierwszą personalizowaną książkę." : "Sign up and we'll send a discount code for your first personalized book."}</p>
          {code ? (
            <div className="mt-6 inline-flex items-center gap-3 rounded-full bg-[#1A243A] text-[#FDFBF7] px-6 py-3" data-testid="newsletter-code">
              <Check size={18} className="text-[#D4AF37]" />
              <span className="text-sm">{lang === "pl" ? "Twój kod:" : "Your code:"}</span>
              <span className="font-mono font-bold text-[#D4AF37] tracking-widest">{code}</span>
            </div>
          ) : (
            <div className="mt-6 flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <div className="relative flex-1">
                <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} placeholder={lang === "pl" ? "Twój e-mail" : "Your email"} data-testid="newsletter-email" className="w-full rounded-full border border-[#E7E0D4] bg-white pl-11 pr-4 py-3 outline-none focus:ring-2 focus:ring-[#D4AF37]/40" />
              </div>
              <button onClick={submit} disabled={loading} data-testid="newsletter-submit" className="inline-flex items-center justify-center gap-2 rounded-full px-7 py-3 bg-[#1A243A] text-[#FDFBF7] font-semibold text-sm hover:bg-[#D4AF37] hover:text-[#1A243A] transition-colors">
                {loading ? <Loader2 className="animate-spin" size={16} /> : <ArrowRight size={16} />}{lang === "pl" ? "Odbieram rabat" : "Get my code"}
              </button>
            </div>
          )}
          <p className="mt-4 text-xs text-[#94A3B8]">{lang === "pl" ? "Bez spamu. Możesz zrezygnować w każdej chwili." : "No spam. Unsubscribe anytime."}</p>
        </div>
      </div>
    </section>
  );
}
